import { GuitarMark } from '../GuitarMark.tsx';
import { EYEBROW, themeOf } from './cmsTheme.ts';

/* ------------------------------------------------------------------ */
/*  AppBand                                                            */
/* ------------------------------------------------------------------ */

interface AppBandProps {
  showGuitar?: boolean;
  heading?: string;
  /** Optional supporting line under the heading. Pass null/'' to omit it. */
  subtitle?: string | null;
}

/**
 * The app-promo band. Fixed to the yellow combination (it is not an editable section), and in v2
 * shaped like every other v2 promo block: one card in the yellow card shade, the copy on the left
 * and the store badges on the right, stacked on mobile.
 *
 * The oversized guitar still sits behind it, bleeding off the left edge — the card is opaque, so
 * the watermark reads as texture in the band around it rather than under the copy.
 */
export function AppBand({
  showGuitar = true,
  heading = 'Få festivalen i lommen',
  subtitle = 'Kunstnere, program, billetter, FAQ og frivillig-login — alt sammen i appen.',
}: AppBandProps = {}) {
  const t = themeOf('yellow');

  return (
    <section className="relative bg-mf-yellow py-10 md:py-16 border-t border-mf-green/20 overflow-hidden">
      {showGuitar && (
        <div className={`absolute left-[-10%] top-1/2 -translate-y-1/2 w-[40vw] aspect-927/1081 pointer-events-none opacity-10 ${t.guitar}`} aria-hidden>
          <GuitarMark className="w-full h-full" />
        </div>
      )}
      <div className="relative max-w-[1200px] mx-auto px-5 md:px-8">
        <div className={`rounded-card p-6 md:p-8 ${t.card.bg} ${t.card.text} flex flex-col gap-6 md:flex-row md:items-center md:justify-between md:gap-10`}>
          <div className="md:flex-1">
            <div className={`${EYEBROW} ${t.eyebrow} mb-2`}>Appen</div>
            <h2 className="font-display text-3xl md:text-[40px] leading-none tracking-[-0.02em]">
              {heading}
            </h2>
            {subtitle && (
              <p className={`mt-3 max-w-xl font-normal ${t.card.muted}`}>
                {subtitle}
              </p>
            )}
          </div>
          <div className="flex shrink-0 flex-wrap gap-3">
            <StoreBadge store="apple" />
            <StoreBadge store="google" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  StoreBadge (internal)                                              */
/* ------------------------------------------------------------------ */

interface StoreBadgeProps {
  store: 'apple' | 'google';
}

/** Real store listings for the Midtfyns Festival app (copied from the current site). */
const STORE_HREF: Record<StoreBadgeProps['store'], string> = {
  apple: 'https://apps.apple.com/dk/app/midtfyns-festival/id6478925881?l=da',
  google: 'https://play.google.com/store/apps/details?id=com.app.midtfynsfestival',
};

function StoreBadge({ store }: StoreBadgeProps) {
  return (
    <a href={STORE_HREF[store]} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-mf-ink text-white rounded-cardSm px-5 py-3 hover:bg-mf-ink/90 transition-colors">
      {store === 'apple' ? (
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor">
          <path d="M16.4 12.5c0-2.4 2-3.6 2.1-3.6-1.2-1.7-3-1.9-3.6-2-1.5-.2-3 .9-3.7.9-.8 0-2-.9-3.3-.9-1.7 0-3.3 1-4.2 2.5-1.8 3.1-.5 7.7 1.3 10.3.9 1.2 1.9 2.6 3.3 2.6 1.3-.1 1.8-.9 3.3-.9 1.5 0 2 .9 3.3.8 1.4 0 2.3-1.3 3.1-2.5.7-1 1.3-2.2 1.7-3.4-3.6-1.4-3.3-5.6-3.3-5.6zm-2.4-7.7c.7-.8 1.1-2 1-3.2-1 .1-2.3.7-3 1.5-.7.7-1.2 2-1 3.1 1.1.1 2.3-.6 3-1.4z"/>
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor">
          <path d="M3.6 1.2c-.4.4-.6 1-.6 1.7v18.2c0 .7.2 1.3.6 1.7l11.5-11.5L3.6 1.2zm13.6 9.6L5.7 0l13.7 7.9c1.1.6 1.1 2.2 0 2.9zm-1.7 1l3.6 2.1c1.1.6 1.1 2.2 0 2.9L15.5 19l-2.5-2.5 2.5-2.5z"/>
        </svg>
      )}
      <span className="text-left leading-tight">
        <span className={`block ${EYEBROW} opacity-80`}>
          {store === 'apple' ? 'Download fra' : 'Hent fra'}
        </span>
        <span className="block text-base font-medium">
          {store === 'apple' ? 'App Store' : 'Google Play'}
        </span>
      </span>
    </a>
  );
}
